// src/pages/TableViewer.tsx
import React, { useState, useEffect } from "react";
import {
    Box, Button, Typography, Menu, MenuItem, useTheme, Dialog, DialogTitle,
    DialogContent, DialogActions, Select, FormControl, InputLabel, useMediaQuery,
    IconButton, CircularProgress, Snackbar, Alert, ListItemButton, Checkbox, ListItemText, ListItem, List,
    Tooltip
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent, CellEditingStoppedEvent } from "ag-grid-community";
import { useParams, useNavigate } from "react-router-dom";

import API from "../api/axiosInstance";
import RelatedRecordsPopup from "../components/RelatedRecordsPopup";
import SearchDialog from "../components/SearchDialog";

import SettingsIcon from "@mui/icons-material/Settings";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../styles/TableViewer.css";

import { TablePage, TableField, TableRecordDTO, TenantFlagRule } from "../types/interfaces";
import { buildColumnDefs } from "../components/forms/columnBuilder";
import { validateInlineRow } from "../components/forms/inlineRowValidator";
import { getEditorForField } from "../components/forms/editorUtils";
import FormRoot from "../components/forms/FormRoot";
import InlineRowWrapper from "../components/forms/InlineRowWrapper";
import { FormProvider } from "../components/forms/FormContext";
import RowActionsMenu from "../components/RowActionsMenu";
import { getBlobLabel, getMimeType, getMimeTypeFromExtension } from "../utils/layoutUtils";
import { createUUID } from "../utils/validators";
import { useLocation } from "react-router-dom";
import BlobViewerDialog from "../components/BlobViewerDialog";

const TableViewer: React.FC = () => {
    const { tableId } = useParams<{ tableId: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();

    const [tableName, setTableName] = useState("Untitled Table");
    const [tableFields, setTableFields] = useState<TableField[]>([]);
    const [records, setRecords] = useState<any[]>([]);
    const [columns, setColumns] = useState<ColDef[]>([]);
    const [loading, setLoading] = useState(true);
    const [gridApi, setGridApi] = useState<any>(null);
    const [inlineRow, setInlineRow] = useState<any | null>(null);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    const [groupByField, setGroupByField] = useState<string | null>(null);
    const [columnOptions, setColumnOptions] = useState<any[]>([]);
    const [SearchDialogOpen, setSearchDialogOpen] = useState(false);
    const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const [tablePages, setTablePages] = useState<TablePage[]>([]);
    const [activeFormMode, setActiveFormMode] = useState<"ADD" | "EDIT" | "VIEW" | null>(null);
    const [activeFormData, setActiveFormData] = useState<any>(null);
    const [activeFormPage, setActiveFormPage] = useState<TablePage | null>(null);
    const [showFormPopup, setShowFormPopup] = useState(false);

    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [activeRowForActionMenu, setActiveRowForActionMenu] = useState<any | null>(null);

    const [tableOptionsOpen, setTableOptionsOpen] = useState(false);
    const [gearAnchorEl, setGearAnchorEl] = useState<null | HTMLElement>(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState("CSV");

    const [blobViewer, setBlobViewer] = useState<{ fieldName: string, type: string, content: any } | null>(null);
    const [attachDialog, setAttachDialog] = useState<{ field: string; label: string } | null>(null);
    const [flagRules, setFlagRules] = useState<TenantFlagRule[]>([]);


    const [permissions, setPermissions] = useState({
        canAdd: true, canInlineAdd: true, canImport: true, canExport: true
    });

    useEffect(() => {
        if (!tableId) return;
        fetchInitialData();
    }, [tableId]);


    
    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (!user) return navigate("/login");
    
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                }
            };
    
            const tableRes = await API.get(`/tables/${tableId}`, headers);
            const table = tableRes.data;
    
            if (!table || !table.tableID) {
                console.warn("Table not found or invalid response", tableRes.data);
                return;
            }
    
            // Default empty arrays if missing
            table.description = table.description ?? "";
            table.fields = Array.isArray(table.fields) ? table.fields : [];
            table.pages = Array.isArray(table.pages) ? table.pages : [];
            table.relationships = Array.isArray(table.relationships) ? table.relationships : [];
    
            setTableName(table.tableName || "Untitled Table");
            setTableFields(table.fields);
            setTablePages(table.pages);

            var rules = [];
            try {
                const res = await API.get(`/tables/flags/${user.user.tenantID}`, headers);
                rules = res.data || [];
                setFlagRules(rules);
          
              } catch (err) {
                console.error("Failed to fetch flag rules", err);
              }


            const colDefs = buildColumnDefs(table.fields, {
                onAttachFile: (field) => handleFileAttach(field),
                onOpenBlob: (field, value) => handleBlobOpen(field, value),
                onOpenActionMenu: (row: any, anchor: HTMLElement) => {
                    setActionMenuAnchorEl(anchor);
                    setActiveRowForActionMenu(row);
                }
            },
            undefined,
            rules);
    
            setColumns(colDefs);
    
            const colOpts = colDefs.map(col => ({
                field: col.field,
                label: col.headerName,
                visible: col.hide !== true,
            }));
    
            setColumnOptions(colOpts);
    
            await fetchPermissions();
    
            // If a SavedFilter was passed during navigation, apply it
            const savedFilter = location?.state?.savedFilter;
            if (savedFilter?.filterJson) {
                try {
                    const parsedFilters = JSON.parse(savedFilter.filterJson);
                    if (Array.isArray(parsedFilters)) {
                        console.log(`Applying saved filter: ${savedFilter.name}`);
                        await fetchTableRecordsWithFilters(parsedFilters);
                    } else {
                        console.warn("Saved filterJson was not an array:", savedFilter.filterJson);
                        await fetchTableRecords(); // fallback
                    }
                } catch (err) {
                    console.error("Failed to parse saved filterJson:", err);
                    await fetchTableRecords(); // fallback
                }
            } else {
                await fetchTableRecords(); // Normal loading
            }
    
        } catch (error) {
            console.error("Failed to load table:", error);
            setRecords([]); // make sure grid does not stay stale
        } finally {
            setLoading(false);
        }
    };
    

//    const getFileIcon = (mime: string) => {
//        if (mime.includes("pdf")) return <PictureAsPdfIcon fontSize="small" />;
//        if (mime.startsWith("image/")) return <ImageIcon fontSize="small" />;
//        return <InsertDriveFileIcon fontSize="small" />;
//      };


const [viewerOpen, setViewerOpen] = useState(false);
const [viewerParams, setViewerParams] = useState<{
fileID: string;
mimeType?: string;
isBlob?: boolean;
label?: string;
} | null>(null);


const fetchFlagRules = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user?.user?.tenantID) return;
    };
  
  const evaluateFlagRules = (row: any): any => {
    for (const rule of flagRules.filter(r => r.isEnabled)) {
      const fieldMatch = tableFields.find(f => f.fieldName === rule.fieldName);
      if (!fieldMatch) continue;
  
      const fieldID = fieldMatch.fieldID;
      const fieldValue = row[fieldID];
      const compareVal = rule.comparisonValue ?? "";

      let isMatch = false;
      if (compareVal === "") {
        switch (rule.comparisonOperator) {
          case "=": isMatch = fieldValue === "" || fieldValue == null; break;
          case "!=": isMatch = fieldValue != null && String(fieldValue).trim() !== ""; break;
          case ">=":
          case "<=": isMatch = false; break;
          case "not contains": isMatch = true; break;
          case "is blank": isMatch = fieldValue == null || String(fieldValue).trim() === ""; break;
          default: break;
        }
      } else {
        switch (rule.comparisonOperator) {
          case "=": isMatch = String(fieldValue) == compareVal; break;
          case "!=": isMatch = String(fieldValue) != compareVal; break;
          case ">": isMatch = fieldValue > compareVal; break;
          case "<": isMatch = fieldValue < compareVal; break;
          case ">=": isMatch = fieldValue >= compareVal; break;
          case "<=": isMatch = fieldValue <= compareVal; break;
          case "contains": isMatch = typeof fieldValue === "string" && fieldValue.includes(compareVal); break;
          case "not contains": isMatch = typeof fieldValue === "string" && !fieldValue.includes(compareVal); break;
          case "is blank": isMatch = fieldValue == null || String(fieldValue).trim() === ""; break;
          default: break;
        }
      }
  
      if (isMatch) {
        return {
          ...row,
          __rowColor: rule.rowColor || "",
          __rowIcon: rule.iconName || "",
          __tooltip: rule.tooltipText || ""
        };
      }
    }
  
    return row;
  };
  
       

    // Fetch records with filters (from SearchDialog)
    const fetchTableRecordsWithFilters = async (filters: any[]) => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = {
            headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId,
            }
        };

        try {
            const response = await API.post(`/tables/${tableId}/rows/query`, {
                tableID: tableId,
                filters: filters || [],
                pageNumber: 1,
                pageSize: 100,
                includeDeleted: false
            }, headers);

            const rows = response.data?.rows ?? [];

            const formatted = rows.map((row: any) => {
                const baseRow = { id: row.recordID, ...row.fieldValues };
                return evaluateFlagRules(baseRow);
              });
              
            setRecords(formatted);
        } catch (err) {
            console.error("❌ Failed to fetch filtered records:", err);
            setSnackbar({ open: true, message: "Failed to fetch filtered records", severity: "error" });
        }
    };


    const fetchTableRecords = async () => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };

        try {
            const response = await API.post(`/tables/${tableId}/rows/query`, {
                tableID: tableId,
                filters: [],
                pageNumber: 1,
                pageSize: 100,
                includeDeleted: false
            }, headers);

            const rows = response.data?.rows;

            if (!Array.isArray(rows)) {
                console.error("Expected 'rows' array in response but got:", response.data);
                setRecords([]);
                return;
            }

            const formatted = rows.map((row: any) => {
                const baseRow = { id: row.recordID, ...row.fieldValues };
                return evaluateFlagRules(baseRow);
              });
              

            setRecords(formatted);
        } catch (err) {
            console.error("❌ Failed to fetch records:", err);
            setRecords([]);
        }
    };


    const fetchPermissions = async () => {
        // Placeholder — replace with actual API logic
        setPermissions({
            canAdd: true,
            canInlineAdd: true,
            canImport: true,
            canExport: true,
        });
    };

    const handleFileAttach = (field: TableField): void => {
        setAttachDialog({ field: field.fieldID, label: field.fieldName });
    };

    const handleBlobOpen = (field: TableField, value: any): void => {
        let fileID: string | undefined = undefined;
        let mimeType: string | undefined = undefined;
      
        if (typeof value === "string" && value.includes(":")) {
          const [extOrType, guid] = value.split(":");
          if (/^[0-9a-fA-F\-]{36}$/.test(guid)) {
            fileID = guid;
            mimeType = getMimeTypeFromExtension(extOrType);
          }
        } else if (/^[0-9a-fA-F\-]{36}$/.test(value)) {
          fileID = value;
        }
      
        if (fileID) {
          setViewerParams({
            fileID,
            mimeType,
            isBlob: field.dataType === "Blob",
            label: field.fieldName
          });
          setViewerOpen(true);
        }
      };
    

    const confirmExport = () => {
        setExportDialogOpen(false);
        alert(`Exporting as ${exportFormat}...`);
    };


    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setActiveRowForActionMenu(null);
    };

    const handleActionSelect = async (action: string, rowData?: any) => {
        if (!rowData) return;
        handleActionMenuClose();

        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = {
            headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId,
            },
        };

        switch (action) {
            case "add":
                openFormPage(action.toUpperCase() as "ADD", rowData);
                break;

            case "view":
            case "edit":
                openFormPage(action.toUpperCase() as "EDIT" | "VIEW", rowData);
                break;
            case "delete":
                if (window.confirm("Are you sure you want to delete this record?")) {
                    try {
                        await API.delete(`/tables/${tableId}/records/${rowData.id}`, headers);
                        fetchTableRecords(); // Refresh
                    } catch (err) {
                        alert("Failed to delete record.");
                    }
                }
                break;
            case "clone":
                try {
                    
                    const res = await API.post(`/tables/${tableId}/records/clone`, { recordId: activeRowForActionMenu.id }, headers);
                    if (res?.data?.id) {
                        navigate(`/tables/${tableId}/edit/${res.data.id}`);
                    }
                } catch (err) {
                    alert("Failed to clone record.");
                }
                break;
        }
    };

    const handleRowEdit = async (event: CellEditingStoppedEvent) => {
        const editedRow = event.data;

        if (editedRow.__isNew) return; // handled via saveInlineRow()

        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };

            const fieldValuesNew: Record<string, string> = Object.fromEntries(
                Object.entries(editedRow)
                  .filter(([key]) => key !== "id")
                  .map(([fieldID, value]) => [fieldID, value != null ? String(value) : ""])
              );
                                                        
            const tableDTO : TableRecordDTO = {
                recordID: tableId || "",
                tableID: tableId || "",
                fieldValues: fieldValuesNew,
                tenantID: user.user.tenantID,
                createdBy: user.user.userID,
                updateBy: user.user.userID
            }

            await API.put(`/tables/${tableId}/records/${editedRow.id}`, tableDTO, headers);
            console.log("✅ Record updated:", editedRow.id);
        } catch (err) {
            console.error("❌ Failed to update record:", err);
            setSnackbar({ open: true, message: "Failed to update row", severity: "error" });
        }
    };

    const openFormPage = (mode: "ADD" | "EDIT" | "VIEW", record?: any) => {
        const isPopup = tablePages.some(p => p.pageType.toUpperCase() === `POPUP ${mode}`);
        const formPage = tablePages.find(p => p.pageType.toUpperCase() === (isPopup ? `POPUP ${mode}` : mode));

        if (!formPage) {
            alert(`No ${mode} page layout found.`);
            return;
        }

        setActiveFormMode(mode);
        setActiveFormPage(formPage);
        setActiveFormData(record || null);
        setShowFormPopup(isPopup);
    };


    const handleGridReady = (params: GridReadyEvent) => {
        setGridApi(params.api);
    };

    const addInlineRow = () => {
        if (inlineRow) return;
        const newRow = { id: `new-${Date.now()}`, __isNew: true };
        setInlineRow(newRow);
        setRecords(prev => [newRow, ...prev]);
    };

    const saveInlineRow = async () => {
        const validationErrors = validateInlineRow(inlineRow, tableFields);

        if (validationErrors.length > 0) {
            setSnackbar({ open: true, message: validationErrors.join(", "), severity: "error" });
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };

            const payload = { ...inlineRow };
            delete payload.id;
            delete payload.__isNew;

            await API.post(`/tables/${tableId}/records`, payload, headers);
            setSnackbar({ open: true, message: "Record saved!", severity: "success" });
            setInlineRow(null);
            fetchTableRecords();
        } catch (err) {
            console.error("Error saving inline row:", err);
            setSnackbar({ open: true, message: "Failed to save row.", severity: "error" });
        }
    };

    const cancelInlineRow = () => {
        setRecords(prev => prev.filter(row => row.id !== inlineRow?.id));
        setInlineRow(null);
    };

    const toggleColumnVisibility = (index: number) => {
        const updated = [...columnOptions];
        updated[index].visible = !updated[index].visible;
        setColumnOptions(updated);
    };

    const moveColumn = (index: number, direction: number) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= columnOptions.length) return;

        const updated = [...columnOptions];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        setColumnOptions(updated);
        setSelectedFieldIndex(newIndex);
    };

    const applyTableOptions = () => {
        const visible = columnOptions.filter(opt => opt.visible);
        const newDefs = buildColumnDefs(
            tableFields,
            {
                onAttachFile: handleFileAttach,
                onOpenBlob: handleBlobOpen,
                onOpenActionMenu: (row: any, anchor: HTMLElement) => {
                    setActionMenuAnchorEl(anchor);
                    setActiveRowForActionMenu(row);
                }

            },
            visible.map(opt => opt.field)
        );

        setColumns(newDefs);
        setTableOptionsOpen(false);
    };

    const handleGroupByChange = (value: string) => {
        setGroupByField(value || null);

        const updated = columns.map(col => {
            if (col.field === value) {
                return {
                    ...col,
                    rowGroup: true,
                    hide: true
                };
            } else if (col.rowGroup) {
                const { rowGroup, hide, ...rest } = col;
                return rest;
            }
            return col;
        });

        setColumns(updated);
    };

    return (
        <Box
            p={3}
            sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                minHeight: "100vh",
            }}
        >
            <Box sx={{ minHeight: "64px" }} ></Box>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <Typography variant="h4" gutterBottom>
                    {tableName}
                </Typography>
                <Button
                    size="small"
                    color="secondary"
                    variant="text"
                    onClick={() => navigate("/tables")}
                    sx={{ textTransform: "none" }}
                >
                    Back to Tables
                </Button>
            </Box>



            {viewerParams && (
            <BlobViewerDialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                fileID={viewerParams.fileID}
                mimeType={viewerParams.mimeType}
                isBlob={viewerParams.isBlob}
                fieldLabel={viewerParams.label}
            />
            )}

            {/* Toolbar */}
            <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" gap={2} mb={2}>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" onClick={() => setSearchDialogOpen(true)}>
                        <SearchIcon fontSize="small" />
                    </Button>
                    {permissions.canAdd && (
                        <Button variant="contained" onClick={() => openFormPage("ADD")} disabled={!!inlineRow}>
                            New Record
                        </Button>
                    )}
                    {permissions.canInlineAdd && (
                        <Button variant="outlined" onClick={addInlineRow} disabled={!!inlineRow}>
                            Inline Add
                        </Button>
                    )}
                </Box>

                <Box display="flex" gap={1}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Group By</InputLabel>
                        <Select
                            value={groupByField || ""}
                            onChange={(e) => handleGroupByChange(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="">No Grouping</MenuItem>
                            {columns.map(col => (
                                <MenuItem key={col.field} value={col.field}>
                                    {col.headerName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {!isMobile && permissions.canImport && (
                        <Button variant="outlined" onClick={() => alert("Import popup not yet ready.")}>Import</Button>
                    )}
                    {!isMobile && permissions.canExport && (
                        <Button variant="outlined" onClick={() => setExportDialogOpen(true)}>Export</Button>
                    )}
                    <IconButton onClick={(e) => setGearAnchorEl(e.currentTarget)}>
                        <SettingsIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Gear Menu */}
            <Menu anchorEl={gearAnchorEl} open={!!gearAnchorEl} onClose={() => setGearAnchorEl(null)}>
                {isMobile && (
                    <>
                        <MenuItem onClick={() => alert("Import popup (TBD)")}>Import Records</MenuItem>
                        <MenuItem onClick={() => setExportDialogOpen(true)}>Export Records</MenuItem>
                    </>
                )}
                <MenuItem onClick={() => { setGearAnchorEl(null); setTableOptionsOpen(true); }}>
                    Table Options
                </MenuItem>
            </Menu>

            {/* Main Grid */}
            {/* Enhanced Main Grid Area */}
            {loading ? (
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <CircularProgress size={20} />
                    <Typography>Loading, please wait...</Typography>
                </Box>
            ) : (
                    <Box
                        flex={1}
                        overflow="hidden"
                        sx={{ display: "flex", flexDirection: "column" }}
                    >
                        <Dialog open={!!inlineRow} onClose={cancelInlineRow} maxWidth="sm" fullWidth>
                            <DialogTitle>New Record</DialogTitle>
                            <DialogContent dividers sx={{ pt: 2 }}>
                                {inlineRow && (
                                    <InlineRowWrapper
                                        fields={tableFields}
                                        initialData={inlineRow}
                                        onSave={async (cleanedRow) => {
                                            try {
                                                const user = JSON.parse(localStorage.getItem('user') || "{}");
                                                const headers = {
                                                    headers: {
                                                        authToken: user.user?.authtoken,
                                                        sessionId: user.user?.sessionId,
                                                    }
                                                };
                                                const fieldValuesNew = Object.fromEntries(
                                                    Object.entries(cleanedRow).map(([fieldID, value]) => [fieldID, value ?? ""])
                                                  );
                                                  
                                                                                            
                                                const tableDTO : TableRecordDTO = {
                                                    recordID: tableId || "",
                                                    tableID: tableId || "",
                                                    fieldValues: fieldValuesNew,
                                                    tenantID: user.user.tenantID,
                                                    createdBy: user.user.userID,
                                                    updateBy: user.user.userID
                                                }

                                                //console.log("TableDTO: ",tableDTO);

                                                await API.post(`/tables/${tableId}/records`, tableDTO, headers);
                                                setSnackbar({ open: true, message: "Record created successfully", severity: "success" });
                                                setInlineRow(null);
                                                fetchTableRecords();
                                            } catch (err) {
                                                console.error("Error creating inline row", err);
                                                setSnackbar({ open: true, message: "Failed to create record", severity: "error" });
                                            }
                                        }}
                                        onCancel={cancelInlineRow}
                                    />
                                )}
                            </DialogContent>
                        </Dialog>

                    <div
                            className={`${theme.palette.mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"}`}
                            style={{ width: "100%", minHeight: 400 }}
                    >
                        <AgGridReact
                            rowData={records}
                            columnDefs={columns}
                            onGridReady={handleGridReady}
                            onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
                            onCellEditingStopped={handleRowEdit}
                            rowSelection="multiple"
                            domLayout="autoHeight"
                            suppressColumnVirtualisation
                            groupDisplayType="multipleColumns"
                            context={{
                                tableId,
                                handleActionSelect, 
                                rowDataMap: records.reduce((map, row) => {
                                  map[row.id] = row;
                                  return map;
                                }, {} as Record<string, any>)
                              }}
                              getRowStyle={(params) => {
                                const row = params.data;
                                if (!row) return undefined;
                              
                                // Map fieldName -> fieldID
                                const fieldNameToIdMap = Object.fromEntries(tableFields.map(f => [f.fieldName, f.fieldID]));
                              
                                const matchedRule = flagRules.find(rule => {
                                  const fieldID = fieldNameToIdMap[rule.fieldName];
                                  if (!fieldID) return false;
                              
                                  const fieldVal = row[fieldID];
                                  const ruleVal = rule.comparisonValue ?? "";
                              
                                  if (ruleVal === "") {
                                    switch (rule.comparisonOperator) {
                                      case "=": return fieldVal === "" || fieldVal == null;
                                      case "!=": return fieldVal != null && String(fieldVal).trim() !== "";
                                      case ">=":
                                      case "<=":
                                        return false;
                                      case "not contains": return true;
                                      case "is blank": return fieldVal == null || String(fieldVal).trim() === "";
                                      default: return false;
                                    }
                                  }
                              
                                  switch (rule.comparisonOperator) {
                                    case "=": return String(fieldVal) == ruleVal;
                                    case "!=": return String(fieldVal) != ruleVal;
                                    case ">": return fieldVal > ruleVal;
                                    case "<": return fieldVal < ruleVal;
                                    case ">=": return fieldVal >= ruleVal;
                                    case "<=": return fieldVal <= ruleVal;
                                    case "contains": return typeof fieldVal === "string" && fieldVal.includes(ruleVal);
                                    case "not contains": return typeof fieldVal === "string" && !fieldVal.includes(ruleVal);
                                    case "is blank": return fieldVal == null || String(fieldVal).trim() === "";
                                    default: return false;
                                  }
                                });
                              
                                if (matchedRule?.rowColor) {
                                  const backgroundColor = matchedRule.rowColor;
                                  const luminance = (hex: string) => {
                                    const c = hex.replace("#", "");
                                    const r = parseInt(c.substring(0, 2), 16);
                                    const g = parseInt(c.substring(2, 4), 16);
                                    const b = parseInt(c.substring(4, 6), 16);
                                    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                                  };
                                  const foregroundColor = luminance(backgroundColor) > 0.5 ? "#000000" : "#FFFFFF";
                                  return { backgroundColor, color: foregroundColor };
                                }
                              
                                return undefined;
                              }}
                              
                              
                            autoGroupColumnDef={{
                                headerName: "Group",
                                minWidth: 200,
                                pinned: "left",
                                cellRendererParams: { suppressCount: false },
                                suppressMovable: true,
                                cellStyle: {
                                    backgroundColor: theme.palette.background.paper,
                                    fontWeight: "bold"
                                }
                            }}
                        />
                    </div>
                </Box>
            )}

            {/* Export Dialog */}
            <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
                <DialogTitle>Select Export Format</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth>
                        <InputLabel>Format</InputLabel>
                        <Select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
                            <MenuItem value="CSV">CSV</MenuItem>
                            <MenuItem value="XLSX">Excel (XLSX)</MenuItem>
                            <MenuItem value="JSON">JSON</MenuItem>
                            <MenuItem value="XML">XML</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmExport}>Export</Button>
                </DialogActions>
            </Dialog>



            {/* Blob Viewer */}
            <Dialog open={!!blobViewer} onClose={() => setBlobViewer(null)}>
                <DialogTitle>{blobViewer?.fieldName}</DialogTitle>
                <DialogContent>
                    {blobViewer?.content ? (
                        <Typography>
                            Content or download info will appear here. (To be implemented)
                        </Typography>
                    ) : (
                        <Typography>No data available.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBlobViewer(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Attach Dialog */}
            <Dialog open={!!attachDialog} onClose={() => setAttachDialog(null)}>
                <DialogTitle>Attach File - {attachDialog?.label}</DialogTitle>
                <DialogContent>
                    <input
                        type="file"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file && attachDialog) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                    setInlineRow((prev: any) => ({
                                        ...prev,
                                        [attachDialog.field]: reader.result
                                    }));
                                    setAttachDialog(null);
                                };
                                reader.readAsDataURL(file);
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAttachDialog(null)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Search Dialog */}
            <SearchDialog
                open={SearchDialogOpen}
                onClose={() => setSearchDialogOpen(false)}
                tableId={tableId}
                fields={tableFields}
                onSubmit={(filters: any) => {
                    fetchTableRecordsWithFilters(filters);
                    setSearchDialogOpen(false);
                }}
            />

            

            {/* Table Options (Customize Columns) */}
            <Dialog open={tableOptionsOpen} onClose={() => setTableOptionsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Customize Table Columns</DialogTitle>
                <DialogContent dividers sx={{ px: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Toggle visibility and rearrange columns to customize your table view.
                    </Typography>
                    <List dense>
                        {columnOptions.map((item, index) => (
                            <ListItem key={item.field} divider>
                                <ListItemButton
                                    selected={selectedFieldIndex === index}
                                    onClick={() => setSelectedFieldIndex(index)}
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={item.visible}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleColumnVisibility(index);
                                        }}
                                    />
                                    <ListItemText primary={item.label} />
                                </ListItemButton>
                                <Box>
                                    <IconButton size="small" onClick={() => moveColumn(index, -1)} disabled={index === 0}>
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => moveColumn(index, 1)} disabled={index === columnOptions.length - 1}>
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTableOptionsOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={applyTableOptions}>Apply</Button>
                </DialogActions>
            </Dialog>


            {/* Snackbar (Status messages) */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={snackbar.severity as any} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>


            {activeFormMode && activeFormPage && (
            <Box
                position="fixed"
                top={0}
                left={0}
                right={0}
                bottom={0}
                zIndex={1300}
                bgcolor={theme.palette.background.paper}
                sx={{
                overflowY: 'auto',
                px: 4,
                py: 3,
                boxShadow: 4,
                }}
            >
                <FormProvider mode={activeFormMode} fields={tableFields} initialData={activeFormData || {}}>
                <FormRoot
                    key={activeFormPage.pageID + "-" + activeFormMode}
                    page={activeFormPage}
                    mode={activeFormMode}
                    recordData={activeFormData || {}}
                    onSave={async (formData) => {
                    const user = JSON.parse(localStorage.getItem('user') || '{}');
                    const headers = {
                        headers: {
                        authToken: user.user?.authtoken,
                        sessionId: user.user?.sessionId,
                        },
                    };

                    const fieldValuesNew: Record<string, string> = Object.fromEntries(
                        Object.entries(formData)
                        .filter(([key]) => key !== "id")
                        .map(([fieldID, value]) => [fieldID, value != null ? String(value) : ""])
                    );

                    const tableDTO: TableRecordDTO = {
                        recordID: activeFormMode === "EDIT" ? activeFormData?.id || createUUID() : createUUID(),
                        tableID: tableId || "",
                        fieldValues: fieldValuesNew,
                        tenantID: user.user.tenantID,
                        createdBy: user.user.userID,
                        updateBy: user.user.userID,
                    };

                    if (activeFormMode === "ADD") {
                        await API.post(`/tables/${tableId}/records`, tableDTO, headers);
                    } else if (activeFormMode === "EDIT") {
                        await API.put(`/tables/${tableId}/records/${tableDTO.recordID}`, tableDTO, headers);
                    }

                    setActiveFormMode(null);
                    setActiveFormData(null);
                    setActiveFormPage(null);
                    fetchTableRecords();
                    }}
                    onClose={() => {
                    setActiveFormMode(null);
                    setActiveFormData(null);
                    setActiveFormPage(null);
                    fetchTableRecords();
                    }}
                    inlineMode={true}
                />
                </FormProvider>
            </Box>
            )}


        </Box>
    );
};

export default TableViewer;
